import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { AppleMap } from "capitalrx-components-2.0";
import { Icon, LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { DrugPricesRequest, Prices, PricesPharmacy, PricesRejectInfo, SubmissionClarificationCode, useListDrugPricesQuery } from "@/App/api/drug";
import { Pharmacy, useListFavoritePharmaciesQuery } from "@/App/api/pharmacy";
import { ContentPanel, MapLocation, NoResults, PageWrapper, Pager, PharmacyTile, ThemedButton } from "@/App/components";
import { basisOfReimbursement } from "@/App/constants/constants";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { extractNumber } from "@/App/utils/formatSelectorDrugQuantity";
import { formatDollars, getPharmacyDescription } from "@/App/utils/utils";
import { FindBestPriceMemberMessageModal } from "./FindBestPriceMemberMessageModal";
import { FindBestPriceRejectModal } from "./FindBestPriceRejectModal";
import { isRxCapProgramCheck } from "./utils";

const SIX_ARRAY = [...Array(6).values()];

export const FindBestPriceResultsPage: FC = () => {
  const { t: translate } = useTranslation();
  const { drug_id: drugId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState(1);
  const [showingRejectModal, setShowingRejectModal] = useState(false);
  const [showingMemberMessageModal, setShowingMemberMessageModal] = useState(false);
  const [suppressableCodes, setSuppresableCodes] = useState([]);
  const navigate = useNavigateClient();

  const dosageForm = searchParams.get("dosage_form");
  const dosage = searchParams.get("dosage");
  const quantity = searchParams.get("quantity");
  const daysSupply = searchParams.get("days_supply");
  const hasShownCapRxMessage = searchParams.get("rxCapModalShown");
  const location: { readonly latitude: string; readonly longitude: string } = JSON.parse(
    decodeURIComponent(searchParams.get("loc") || "{}"),
  );

  const submissionClarificationCode = useMemo(() => {
    try {
      return JSON.parse(searchParams.get("submission_clarification_code"));
    } catch (err) {
      console.log(err);
    }
    return null;
  }, [searchParams]);

  const setSubmissionClarificationCode = useCallback((submissionClarificationCode: SubmissionClarificationCode | null) => {
    setSearchParams((prev) => {
      const upcoming = new URLSearchParams(prev);
      if (submissionClarificationCode == null) {
        upcoming.delete("submission_clarification_code");
      } else {
        upcoming.set("submission_clarification_code", JSON.stringify(submissionClarificationCode));
      }
      return upcoming;
    });
  }, [setSearchParams]);

  const drugPricesRequest: DrugPricesRequest = useMemo(() => {
    return {
      drug_id: +drugId,
      dosage_form: dosageForm,
      dosage: dosage,
      quantity: +extractNumber(quantity),
      days_supply: +extractNumber(daysSupply),
      location: location,
      page_number: pageNumber,
      override_codes: suppressableCodes,
      submission_clarification_code: submissionClarificationCode,
    };
  }, [dosageForm, dosage, quantity, daysSupply, location, drugId, pageNumber, suppressableCodes, submissionClarificationCode]);

  const { data: drugPrices, isLoading: isLoadingDrugPrices, isError: isLoadingDrugsError } = useListDrugPricesQuery(drugPricesRequest);
  const { data: favoritePharmacies } = useListFavoritePharmaciesQuery();

  const rejectInfo = useMemo(() => {
    if (drugPrices) {
      return drugPrices.reject_info;
    }
    return { reject_message_list: [] } as PricesRejectInfo;
  }, [drugPrices]);

  const memberMessages = useMemo(() => {
    if (drugPrices) {
      return drugPrices.custom_member_messages || [];
    } else {
      return [];
    }
  }, [drugPrices]);

  const drug = useMemo(() => {
    return drugPrices?.prices[0]?.drug;
  }, [drugPrices]);

  const getDrugPriceDisplay = useCallback((drugPrice: Prices) => {
    const basisOfReimbursementCode = drugPrice.claim.response.response_pricing?.basis_of_reimbursement_determination;
    if (basisOfReimbursementCode && basisOfReimbursement[basisOfReimbursementCode] === "usual_and_customary") {
      return translate("FIND_BEST_PRICE.findBestPriceResultsPage.priceUnavailable");
    } else {
      return formatDollars(drugPrice.claim.response?.response_pricing?.patient_pay_amount);
    }
  }, []);

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const pharmacies: (PricesPharmacy & { readonly patient_pay_amount: string })[] = useMemo(() => {
    if (drugPrices) {
      return drugPrices.prices.map((p) => {
        return {
          ...p.pharmacy,
          patient_pay_amount: formatDollars(p?.claim?.response?.response_pricing?.patient_pay_amount || "0"),
        };
      });
    }
    return [];
  }, [drugPrices]);

  const mapLocations: MapLocation[] = useMemo(() => {
    return pharmacies
      .filter((p) => p.pharmacy_display_rules?.display_on_map !== "never")
      .map((p) => {
        return {
          latitude: p.latitude,
          longitude: p.longitude,
          displayTitle: p.name,
          displayDescription: getPharmacyDescription(p as unknown as Pharmacy, location),
        };
      });
  }, [pharmacies, location]);

  /**
   * TODO -- remove this and push this to the backend so that we're not sorting on
   * only the page here. That is a lot of work tho :(
   */
  const listLocations = useMemo(() => {
    const favoriteProviderIds = (favoritePharmacies?.results || []).map((p) => p.pharmacy.provider_id);
    const sortedPharmacies = (drugPrices?.prices || [])
      .map((p, idx) => {
        return {
          ...p,
          originalIndex: idx,
        };
      })
      .sort((p1, p2) => {
        if (favoriteProviderIds.includes(p1.pharmacy.provider_id) && !favoriteProviderIds.includes(p2.pharmacy.provider_id)) {
          return -1;
        } else if (!favoriteProviderIds.includes(p1.pharmacy.provider_id) && favoriteProviderIds.includes(p2.pharmacy.provider_id)) {
          return 1;
        } else {
          return p1.originalIndex - p2.originalIndex;
        }
      });
    return sortedPharmacies;
  }, [favoritePharmacies, drugPrices]);

  const loadingElements = useMemo(() => {
    return SIX_ARRAY.map((_, idx) => {
      return <LoadingSkeleton key={idx} style={{ width: "100%", height: 64 }} />;
    });
  }, []);

  const onPageChange = useCallback((page: number) => {
    logEvent(`${ANALYTIC_EVENTS.FIND_BEST_PRICE.pagination.pageNumber} ${page}`);
    setPageNumber(page);
  }, []);

  const pager = useMemo(() => {
    return (
      <Pager
        disabled={isLoadingDrugPrices}
        pageNumber={pageNumber}
        setPageNumber={onPageChange}
        resultCount={drugPrices?.result_count}
        resultsPerPage={6}
      />
    );
  }, [isLoadingDrugPrices, pageNumber, setPageNumber, drugPrices]);

  const onBackPressed = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.FIND_BEST_PRICE.backButton);
    navigate(`/find-best-price/${drugId}`);
  }, [drugId]);

  const { allRejectCodes, allPrograms } = useMemo(() => {
    const allRejectCodes = new Set<string>();
    const allPrograms = new Set<string>();
    if (drugPrices) {
      drugPrices.prices.forEach((p) => {
        for (const reject_code of p.claim?.response?.response_status?.reject_codes || []) {
          allRejectCodes.add(reject_code.reject_code);
        }
        for (const program of p.claim?.programs || []) {
          allPrograms.add(program);
        }
      });
    }
    return {
      allRejectCodes: [...allRejectCodes],
      allPrograms: [...allPrograms],
    };
  }, [drugPrices]);

  const hasRxCapProgram = isRxCapProgramCheck(allPrograms);

  const onDrugSelected = useCallback(
    (prices: Prices) => {
      const infoParams = new URLSearchParams();
      infoParams.set("dosage_form", dosageForm);
      infoParams.set("dosage", dosage);
      infoParams.set("quantity", quantity);
      infoParams.set("days_supply", daysSupply);
      infoParams.set("loc", searchParams.get("loc"));
      infoParams.set("provider_id", prices.pharmacy.provider_id);
      const submissionClarificationCode = searchParams.get("submission_clarification_code");
      if (submissionClarificationCode != null) {
        infoParams.set("submission_clarification_code", submissionClarificationCode);
      }
      if (hasShownCapRxMessage) {
        infoParams.set("rxCapModalShown", "true");
      }

      const claimStatus = prices.claim?.transmission?.claim_status;
      const basisOfReimbursementCode = prices.claim.response.response_pricing?.basis_of_reimbursement_determination;

      let rejectMessage: string | undefined = undefined;
      const isUsualAndCustomary = basisOfReimbursementCode && basisOfReimbursement[basisOfReimbursementCode] === "usual_and_customary";

      if (claimStatus === "P" && !isUsualAndCustomary) {
        infoParams.set("patient_pay", encodeURIComponent(prices.claim.response.response_pricing.patient_pay_amount || "0"));
        infoParams.set("plan_pay", encodeURIComponent(prices.claim.response.response_pricing.total_amount_paid || "0"));
      } else {
        if (isUsualAndCustomary) {
          rejectMessage = `${translate("FIND_BEST_PRICE.findBestPriceResultsPage.thePriceFor")} ${drug.drug_name} ${translate("FIND_BEST_PRICE.findBestPriceResultsPage.ifPurchasedThePrice")}`;
        } else {
          rejectMessage = prices.claim.response.response_status.reject_message;
        }

        if (!rejectMessage || rejectMessage === "") {
          const additionalMessages = prices.claim.response.response_status.additional_messages;
          if (additionalMessages) {
            rejectMessage = additionalMessages.reduce((message, additional_message) => {
              message += additional_message.additional_message_information;

              // for cases where multiple rejects can come back. add new line
              if (!additional_message.additional_message_information_continuity) {
                message += "\n";
              }

              return message;
            }, "");
          }
        }

        infoParams.set("reject_message", encodeURIComponent(rejectMessage));
      }

      logEvent(ANALYTIC_EVENTS.FIND_BEST_PRICE.resultItems);
      logEvent(ANALYTIC_EVENTS.FIND_BEST_PRICE.selectedPharmacy);

      navigate({
        pathname: `/find-best-price/${drugId}/pricing-info`,
        search: infoParams.toString(),
      });
    },
    [searchParams, drugId, rejectInfo, drug],
  );

  // if there's reject info, pop the modal
  useEffect(() => {
    setShowingRejectModal(rejectInfo.reject_message_list.length > 0 || (hasRxCapProgram && !hasShownCapRxMessage));
  }, [rejectInfo, allPrograms]);

  // if there's custom member messages, pop that modal
  useEffect(() => {
    setShowingMemberMessageModal(memberMessages.length > 0);
  }, [memberMessages]);

  const onModalDismissed = (codesToSuppress: string[], submissionClarificationCode: null | SubmissionClarificationCode) => {
    if (hasRxCapProgram && !hasShownCapRxMessage) {
      setSearchParams((prev) => {
        prev.set("rxCapModalShown", "true");
        return prev;
      });
    }
    setShowingRejectModal(false);
    if (submissionClarificationCode) {
      setSubmissionClarificationCode(submissionClarificationCode);
    }
    if (codesToSuppress.length > 0) {
      setSuppresableCodes(codesToSuppress);
    }
  };

  return (
    <PageWrapper>
      <FindBestPriceRejectModal
        rejectCodes={allRejectCodes}
        hasShownCapRxMessage={!!hasShownCapRxMessage}
        programs={allPrograms}
        drug={drug}
        isVisible={showingRejectModal}
        rejectInfo={rejectInfo}
        onDismiss={onModalDismissed}
      />
      <FindBestPriceMemberMessageModal
        onClose={() => setShowingMemberMessageModal(false)}
        customMessages={memberMessages}
        isVisible={showingMemberMessageModal}
      />
      <ContentPanel
        title={
          <LoadingGroup loading={isLoadingDrugPrices}>
            <Shelf alignment="center">
              <Stack spacing="small">
                <Shelf>
                  <ThemedButton testId="go-back" onClick={onBackPressed} appearance="secondary" icon="arrow-left">
                    {translate("FIND_BEST_PRICE.findBestPriceResultsPage.back")}
                  </ThemedButton>

                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 36, width: 120 }} />}>
                    <Subtitle testId="drug-name">{drugPrices?.prices[0]?.drug?.drug_name}</Subtitle>
                  </LoadingGroup.Placeholder>
                  <Shelf.Spacer />
                </Shelf>
                <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 24, width: 220 }} />}>
                  <Body testId="drug-details">
                    {dosageForm} | {dosage} | {quantity} | {daysSupply} {translate("FIND_BEST_PRICE.findBestPriceResultsPage.daysSupply")}
                  </Body>
                </LoadingGroup.Placeholder>
              </Stack>
              <Shelf.Spacer />
            </Shelf>
          </LoadingGroup>
        }
      >
        {!isLoadingDrugsError && (
          <Grid>
            <Grid.Span columns={isCompressed ? 12 : 6}>
              <LoadingGroup loading={isLoadingDrugPrices}>
                <LoadingGroup.Placeholder fallback={<Stack spacing="small">{loadingElements}</Stack>}>
                  <Stack>
                    <Paragraph>
                      {translate("FIND_BEST_PRICE.findBestPriceResultsPage.theMemberResponsabilityMayVary")}
                    </Paragraph>
                    <Semibold>{translate("FIND_BEST_PRICE.findBestPriceResultsPage.estimadedAmountYouWillPay")}</Semibold>
                    <Stack spacing="small" testId="pharmacy-pricings-container">
                      {listLocations.map((drugPrice, idx) => {
                        return (
                          <PharmacyTile
                            key={drugPrice.pharmacy.provider_id}
                            pharmacy={drugPrice.pharmacy as unknown as Pharmacy}
                            index={idx + 6 * (pageNumber - 1)}
                            userLocation={location}
                            isLoading={isLoadingDrugPrices}
                            button={
                              <ThemedButton
                                testId={`go-to-pharmacy-${drugPrice.pharmacy.provider_id}-pricing`}
                                onClick={() => onDrugSelected(drugPrice)}
                                icon="chevron-right"
                                iconPosition="right"
                              >
                                <Stack alignment="center">
                                  {drugPrice.claim.transmission.claim_status === "P" && getDrugPriceDisplay(drugPrice)}
                                  {drugPrice.claim.transmission.claim_status === "R" && <Icon name="x-circle" />}
                                </Stack>
                              </ThemedButton>
                            }
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Grid.Span>

            {isCompressed && <Grid.Span columns={12}>{pager}</Grid.Span>}

            <Grid.Span columns={isCompressed ? 12 : 6}>
              {!isLoadingDrugPrices && <AppleMap
                style={{
                  height: isCompressed ? "400px" : "100%",
                }}
                renderCalloutContent={(location) => <div>{location.data.displayDescription}</div>}
                locations={mapLocations.map((location) => ({
                  latitude: parseFloat(location.latitude),
                  longitude: parseFloat(location.longitude),
                  title: location.displayTitle,
                  data: {
                    displayDescription: location.displayDescription,
                  },
                }))}
              />}
            </Grid.Span>

            {!isCompressed && (
              <Grid.Span columns={12}>
                <Shelf>
                  <Shelf.Spacer />
                  {pager}
                  <Shelf.Spacer />
                </Shelf>
              </Grid.Span>
            )}
          </Grid>
        )}
        {isLoadingDrugsError && (
          <NoResults message={translate("FIND_BEST_PRICE.findBestPriceResultsPage.anErrorOccured")} />
        )}
      </ContentPanel>
    </PageWrapper>
  );
};
